import { isEqual } from 'lodash'
import React, { useCallback, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { isPlacable } from '../../../types/ModuleData'
import Sidebar from '../../components/sidebar/Sidebar'
import SidebarModuleButton from '../../components/sidebar/SidebarModuleButton'
import SidebarSection from '../../components/sidebar/SidebarSection'
import { useDispatch, useSelector } from '../../store/hooks'
import GlobalPropertyOverlay, { GlobalPropertyOverlayRef } from './GlobalPropertyOverlay'
import SidebarGlobalPropertySection from './SidebarGlobalPropertySection'
import { useTranslation } from '../../store/hooks/dataHooks'
import Button from '../../components/Button'
import { setModeAction } from '../../store/actions/uiActions'
import ModulePropertyOverlay from './ModulePropertyOverlay'
import { useDragDropHandler } from '../../components/dnd/DragDropContext'
import SidebarButton from '../../components/sidebar/SidebarButton'
import ApplicationOverlay from './ApplicationOverlay'

export type ExpertModeProps = {
  className?: string
}

const Fullscreen = styled.div`
  position: absolute;
  top: -55px;
  padding: 15px;
  padding-top: 70px;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  z-index: 1000;
  display: flex;
  justify-content: center;
  text-align: center;
  align-items: center;
  pointer-events: all;
  flex-direction: column;
`

const OverlayWrap = styled.div`
  flex: 1;
  pointer-events: none;
  position: relative;
  z-index: 5;

  > * {
    pointer-events: auto;
  }
`

const ExpertMode = styled<React.FC<ExpertModeProps>>(function ExpertMode({ className }) {
  const modules = useSelector(
    (state) =>
      state.data.modules
        .filter(isPlacable)
        .filter(({ type }) => type !== 'LightingHead' && type !== 'SupportingColumn'),
    isEqual
  )
  const overlay = useRef<GlobalPropertyOverlayRef>(null)
  const handleSectionChange = useCallback((nextSection: string) => {
    requestAnimationFrame(() => {
      overlay.current?.scrollToSection(nextSection)
    })
  }, [])
  const t = useTranslation()

  const isMobile = useSelector((state) => state.ui.screenSize.width < 992)
  const dispatch = useDispatch()
  const gotoInspiration = useCallback(() => {
    dispatch(setModeAction('inspiration'))
  }, [dispatch])

  useDragDropHandler(
    useMemo(
      () => ({
        onBeforeCapture() {
          dispatch({
            type: 'UI/Expert/ShowPropertyOverlay',
            payload: undefined
          })
        }
      }),
      [dispatch]
    )
  )

  const showApplications = useCallback(
    () =>
      dispatch({
        type: 'UI/Expert/ShowApplicationOverlay',
        show: true
      }),
    [dispatch]
  )
  const applicationsVisible = useSelector((state) => state.ui.expert.showApplications)

  if (isMobile) {
    return (
      <div className={className}>
        <Fullscreen>
          <p>{t('expert.responsive.text')}</p>
          <Button cta onClick={gotoInspiration}>
            {t('expert.responsive.button')}
          </Button>
        </Fullscreen>
      </div>
    )
  }

  return (
    <div className={className}>
      <Sidebar>
        <SidebarGlobalPropertySection onSectionChange={handleSectionChange} />
        <SidebarSection>
          <SidebarButton icon="nearby-square" onClick={showApplications} active={applicationsVisible}>
            {t('ui.presets')}
          </SidebarButton>
        </SidebarSection>
        <SidebarSection title="Extras" badge="neu" description={t('expert.menu.drag')}>
          {modules.map((module) => (
            <SidebarModuleButton module={module} key={module.id} />
          ))}
        </SidebarSection>
      </Sidebar>
      <ModulePropertyOverlay />
      <OverlayWrap>
        <GlobalPropertyOverlay ref={overlay} />
        <ApplicationOverlay />
      </OverlayWrap>
    </div>
  )
})`
  height: 100%;
  pointer-events: none;
  display: flex;
  flex-direction: row;

  ${Sidebar} {
    pointer-events: auto;
    z-index: 10;
  }
`

export default ExpertMode
